import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, RefreshCw, Star, UserCheck, MapPin, UserPlus, Users, Clock, WifiOff, CreditCard, Calendar, Play, Pause, Pencil, X, Smartphone, Globe, Trash2, Building2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const DriversManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("my-drivers");
  const [drivers, setDrivers] = useState([]);
  const [riderRequests, setRiderRequests] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // For debounced search
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCityDialog, setShowCityDialog] = useState(false);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState("");
  const [claimCityId, setClaimCityId] = useState("");
  const [claimingDriver, setClaimingDriver] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: "", email: "", phone: "", password: "", vehicle_type: "", vehicle_number: "" });
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [vehicleSubtypes, setVehicleSubtypes] = useState([]);
  const [commissionPlans, setCommissionPlans] = useState([]);
  const [riderRequestSearch, setRiderRequestSearch] = useState("");
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Sort cities alphabetically
  const sortedCities = [...cities].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  useEffect(() => {
    if (user?.id) {
      fetchRiderRequests();
      fetchCities();
    }
  }, [user?.id]);

  // Fetch drivers when filters change (server-side)
  useEffect(() => {
    if (user?.id) {
      fetchDrivers();
    }
  }, [user?.id, page, searchTerm, statusFilter, cityFilter]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      // Use paginated endpoint with server-side filtering
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        include_unclaimed: "false"
      });
      
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (cityFilter && cityFilter !== "all") params.append("city_id", cityFilter);
      
      const res = await axios.get(`${API}/admin/${user.id}/drivers/paginated?${params.toString()}`);
      
      setDrivers(res.data.drivers || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (err) {
      toast.error("Failed to load drivers");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRiderRequests = async () => {
    try {
      const res = await axios.get(`${API}/admin/rider-requests?admin_id=${user.id}`);
      setRiderRequests(res.data);
    } catch (err) {
      console.error("Failed to load rider requests", err);
    }
  };

  const dismissRiderRequest = async (driverId, driverName) => {
    try {
      await axios.put(`${API}/admin/${user.id}/dismiss-rider-request/${driverId}`);
      toast.success(`${driverName} removed from your list`);
      fetchRiderRequests();
    } catch (err) {
      toast.error("Failed to dismiss rider request");
    }
  };

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities?admin_id=${user.id}`);
      // Sort cities alphabetically and filter active
      const sortedCities = (res.data || []).filter(c => c.is_active).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      console.error("Failed to load cities", err);
    }
  };

  const fetchVehicleSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/vehicle-settings/${user.id}`);
      setVehicleSubtypes(res.data);
    } catch (err) {
      console.error("Failed to load vehicle settings", err);
    }
  };

  const fetchCommissionPlans = async () => {
    try {
      const res = await axios.get(`${API}/commission/plans/${user.id}`);
      setCommissionPlans(res.data);
    } catch (err) {
      console.error("Failed to load commission plans", err);
    }
  };

  const openEditDialog = (driver) => {
    setEditingDriver({
      ...driver,
      city_id: driver.city_id || "",
      commission_plan_id: driver.commission_plan_id || ""
    });
    fetchVehicleSettings();
    fetchCommissionPlans();
    setShowEditDialog(true);
  };

  const handleUpdateDriver = async () => {
    if (!editingDriver) return;
    
    try {
      // Convert __none__ values to null for backend
      const cityId = editingDriver.city_id === "__none__" ? null : editingDriver.city_id;
      const commissionPlanId = editingDriver.commission_plan_id === "__none__" ? null : editingDriver.commission_plan_id;
      const vehicleSubtype = editingDriver.vehicle_subtype === "__none__" ? null : editingDriver.vehicle_subtype;
      
      await axios.put(`${API}/auth/users/${editingDriver.id}`, {
        name: editingDriver.name,
        phone: editingDriver.phone,
        vehicle_type: editingDriver.vehicle_type,
        vehicle_subtype: vehicleSubtype || null,
        vehicle_number: editingDriver.vehicle_number,
        city_id: cityId || null,
        commission_plan_id: commissionPlanId || null
      });
      
      toast.success("Driver updated successfully");
      setShowEditDialog(false);
      setEditingDriver(null);
      fetchDrivers();
    } catch (err) {
      // Handle FastAPI validation errors properly
      const detail = err.response?.data?.detail;
      let errorMsg = "Failed to update driver";
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || detail[0]?.message || "Validation error";
      }
      toast.error(errorMsg);
    }
  };

  // Subscription Management Functions
  const openSubscriptionDialog = async (driver) => {
    setSelectedDriver(driver);
    setShowSubscriptionDialog(true);
    setSubscriptionLoading(true);
    
    try {
      const res = await axios.get(`${API}/admin/driver/${driver.id}/subscription?admin_id=${user.id}`);
      setSubscriptionData(res.data);
    } catch (err) {
      toast.error("Failed to load subscription details");
      setSubscriptionData(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const updateSubscription = async (updates) => {
    if (!selectedDriver) return;
    
    try {
      await axios.put(`${API}/admin/driver/${selectedDriver.id}/subscription?admin_id=${user.id}`, updates);
      toast.success("Subscription updated successfully");
      // Refresh data
      const res = await axios.get(`${API}/admin/driver/${selectedDriver.id}/subscription?admin_id=${user.id}`);
      setSubscriptionData(res.data);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update subscription");
    }
  };

  const activateSubscription = async (extendDays = null) => {
    if (!selectedDriver) return;
    
    try {
      const url = extendDays 
        ? `${API}/admin/driver/${selectedDriver.id}/subscription/activate?admin_id=${user.id}&extend_days=${extendDays}`
        : `${API}/admin/driver/${selectedDriver.id}/subscription/activate?admin_id=${user.id}`;
      const res = await axios.post(url);
      toast.success(res.data.message);
      // Refresh data
      const subRes = await axios.get(`${API}/admin/driver/${selectedDriver.id}/subscription?admin_id=${user.id}`);
      setSubscriptionData(subRes.data);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate subscription");
    }
  };

  const deactivateSubscription = async () => {
    if (!selectedDriver) return;
    
    try {
      await axios.post(`${API}/admin/driver/${selectedDriver.id}/subscription/deactivate?admin_id=${user.id}`);
      toast.success("Subscription deactivated");
      // Refresh data
      const res = await axios.get(`${API}/admin/driver/${selectedDriver.id}/subscription?admin_id=${user.id}`);
      setSubscriptionData(res.data);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deactivate subscription");
    }
  };

  const handleCreateDriverByPhone = async () => {
    if (!newDriver.name || !newDriver.phone) {
      toast.error("Please enter name and phone number");
      return;
    }
    if (newDriver.phone.length < 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    try {
      await axios.post(`${API}/admin/drivers/phone?admin_id=${user.id}`, {
        name: newDriver.name,
        phone: newDriver.phone,
        vehicle_type: newDriver.vehicle_type || null,
        vehicle_number: newDriver.vehicle_number || null,
      });
      toast.success("Driver created successfully! They can login with Phone OTP.");
      setShowAddDialog(false);
      setNewDriver({ name: "", email: "", phone: "", password: "", vehicle_type: "", vehicle_number: "" });
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create driver");
    }
  };

  const openCityDialog = (driver) => {
    setSelectedDriver(driver);
    setSelectedCityId(driver.assigned_city_id || "");
    setShowCityDialog(true);
  };

  const handleAssignCity = async () => {
    try {
      const cityParam = selectedCityId ? `?city_id=${selectedCityId}&admin_id=${user.id}` : `?admin_id=${user.id}`;
      await axios.put(`${API}/admin/driver/${selectedDriver.id}/city${cityParam}`);
      toast.success(selectedCityId ? "City assigned successfully" : "City assignment removed");
      setShowCityDialog(false);
      setSelectedDriver(null);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign city");
    }
  };

  const makeDriverOffline = async (driverId, driverName) => {
    try {
      await axios.put(`${API}/driver/${driverId}/status`, {
        is_online: false
      });
      toast.success(`${driverName || 'Driver'} is now offline`);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update driver status");
    }
  };

  const openClaimDialog = (driver) => {
    setSelectedDriver(driver);
    setClaimCityId("");
    setShowClaimDialog(true);
  };

  const handleClaimDriver = async () => {
    if (!selectedDriver) return;
    setClaimingDriver(true);
    try {
      // Claim the driver
      await axios.put(`${API}/admin/${user.id}/claim-driver/${selectedDriver.id}`);
      
      // If city selected (not "none"), assign it
      if (claimCityId && claimCityId !== "none") {
        await axios.put(`${API}/admin/driver/${selectedDriver.id}/city?city_id=${claimCityId}&admin_id=${user.id}`);
      }
      
      toast.success(`${selectedDriver.name} has been added to your drivers!`);
      setShowClaimDialog(false);
      setSelectedDriver(null);
      setClaimCityId("");
      fetchDrivers();
      fetchRiderRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to claim driver");
    } finally {
      setClaimingDriver(false);
    }
  };

  const toggleApproval = async (driverId, currentApproval) => {
    try {
      await axios.put(`${API}/admin/drivers/${driverId}/approve?approved=${!currentApproval}`);
      toast.success(`Driver ${!currentApproval ? 'approved' : 'unapproved'}`);
      fetchDrivers();
    } catch (err) {
      toast.error("Failed to update approval");
    }
  };

  const toggleStatus = async (driverId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/drivers/${driverId}/status?is_active=${!currentStatus}`);
      toast.success(`Driver ${!currentStatus ? 'enabled' : 'disabled'}`);
      fetchDrivers();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  // Remove driver from admin's list
  const handleRemoveDriver = async (driver, permanently = false) => {
    const action = permanently ? "permanently delete" : "remove";
    const warning = permanently 
      ? `This will PERMANENTLY DELETE ${driver.name}'s account. This cannot be undone!`
      : `This will remove ${driver.name} from your driver list. They will need to request to join again.`;
    
    if (!window.confirm(`${warning}\n\nAre you sure you want to ${action} this driver?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/${user.id}/drivers/${driver.id}?permanently_delete=${permanently}`);
      toast.success(permanently 
        ? `Driver "${driver.name}" has been permanently deleted`
        : `Driver "${driver.name}" has been removed from your list`
      );
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove driver");
    }
  };

  // No longer show loading spinner on initial load - show skeleton/table instead
  // if (loading) return <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="drivers-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Drivers</h2>
          <p className="text-muted-foreground">Manage your drivers and rider requests</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-driver-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Driver
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="my-drivers" className="flex items-center gap-2" data-testid="my-drivers-tab">
            <Users className="w-4 h-4" /> My Drivers ({total})
          </TabsTrigger>
          <TabsTrigger value="rider-requests" className="flex items-center gap-2 relative" data-testid="rider-requests-tab">
            <UserPlus className="w-4 h-4" /> Rider Requests
            {riderRequests.length > 0 && (
              <span className="ml-1 bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
                {riderRequests.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* My Drivers Tab */}
        <TabsContent value="my-drivers">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, phone, or referral code..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
                data-testid="driver-search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border rounded-md bg-background"
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
            <select
              value={cityFilter}
              onChange={(e) => { setCityFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border rounded-md bg-background"
            >
              <option value="all">All Cities</option>
              <option value="unassigned">Unassigned</option>
              {sortedCities.map((city) => (
                <option key={city.id} value={city.id}>{city.name}</option>
              ))}
            </select>
            <Button variant="outline" onClick={fetchDrivers} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">
              {loading ? "Loading..." : `Showing ${drivers.length} of ${total} drivers`}
            </p>
            {totalPages > 1 && (
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
            )}
          </div>

          {/* Drivers Table */}
          <div className="border rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>App Ver</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : drivers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      No drivers found
                    </TableCell>
                  </TableRow>
                ) : (
                  drivers.map((driver) => (
                    <TableRow key={driver.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${driver.is_online ? 'bg-green-100' : 'bg-gray-100'}`}>
                            <span className="text-lg">{driver.name?.[0]?.toUpperCase() || '?'}</span>
                          </div>
                          <div>
                            <p className="font-medium">{driver.name}</p>
                            <p className="text-xs text-muted-foreground">{driver.referral_code}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{driver.email}</p>
                        <p className="text-xs text-muted-foreground">{driver.phone}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm capitalize">{driver.vehicle_type || '-'}</p>
                        <p className="text-xs text-muted-foreground">{driver.vehicle_number || '-'}</p>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openCityDialog(driver)}
                          className="flex items-center gap-1"
                          data-testid={`assign-city-btn-${driver.id}`}
                        >
                          <MapPin className="w-3 h-3" />
                          {driver.assigned_city_name || 'Assign'}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {driver.using_app ? (
                          <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 w-fit ${
                            driver.using_app === 'iOS App' 
                              ? 'bg-gray-100 text-gray-700' 
                              : driver.using_app === 'Android App'
                              ? 'bg-green-100 text-green-700'
                              : driver.using_app === 'Native App'
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {driver.using_app === 'iOS App' 
                              ? <Smartphone className="w-3 h-3" /> 
                              : driver.using_app === 'Android App'
                              ? <Smartphone className="w-3 h-3" />
                              : driver.using_app === 'Native App'
                              ? <Smartphone className="w-3 h-3" />
                              : <Globe className="w-3 h-3" />
                            }
                            {driver.using_app}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {driver.app_version ? (
                          <span className={`px-2 py-1 rounded text-xs font-mono ${
                            driver.app_version === '1.0.11' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            v{driver.app_version}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          driver.account_status === 'deleted' ? 'bg-gray-200 text-gray-600' :
                          driver.is_online ? 'bg-green-100 text-green-700' :
                          driver.approval_status === 'approved' ? 'bg-blue-100 text-blue-700' :
                          driver.approval_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {driver.account_status === 'deleted' ? 'Deleted' : driver.is_online ? 'Online' : driver.approval_status || 'Pending'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span>{driver.rating?.toFixed(1) || '5.0'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{new Date(driver.created_at).toLocaleDateString()}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs gap-1"
                            onClick={() => openEditDialog(driver)}
                            data-testid={`edit-driver-btn-${driver.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                            Edit
                          </Button>
                          <Switch
                            checked={driver.approval_status === 'approved'}
                            onCheckedChange={() => toggleApproval(driver.id, driver.approval_status === 'approved')}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs gap-1"
                            onClick={() => openSubscriptionDialog(driver)}
                            data-testid={`subscription-btn-${driver.id}`}
                          >
                            <CreditCard className="w-3 h-3" />
                            Plan
                          </Button>
                          {driver.is_online && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs gap-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                              onClick={() => makeDriverOffline(driver.id, driver.name)}
                              data-testid={`make-offline-${driver.id}`}
                            >
                              <WifiOff className="w-3 h-3" />
                              Offline
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs gap-1 text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleRemoveDriver(driver, false)}
                            title="Remove driver from your list"
                            data-testid={`remove-driver-${driver.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                            Remove
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1 || loading}
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1 || loading}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 text-sm">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages || loading}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages || loading}
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Rider Requests Tab */}
        <TabsContent value="rider-requests" data-testid="rider-requests-content">
          <div className="mb-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              These are riders who signed up independently. Click "Add to My Drivers" to claim them.
            </p>
            
            {/* Search for Rider Requests */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or phone..."
                value={riderRequestSearch}
                onChange={(e) => setRiderRequestSearch(e.target.value)}
                className="pl-10"
                data-testid="rider-request-search"
              />
            </div>
          </div>

          {riderRequests.filter(r => {
            if (!riderRequestSearch) return true;
            const search = riderRequestSearch.toLowerCase();
            return (
              r.name?.toLowerCase().includes(search) ||
              r.phone?.includes(search)
            );
          }).length === 0 ? (
            <div className="text-center py-12 border rounded-lg bg-muted/30">
              <UserCheck className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-muted-foreground">
                {riderRequestSearch ? "No matching rider requests" : "No pending rider requests"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {riderRequestSearch ? "Try a different search term" : "All riders have been claimed by admins"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {riderRequests
                .filter(r => {
                  if (!riderRequestSearch) return true;
                  const search = riderRequestSearch.toLowerCase();
                  return (
                    r.name?.toLowerCase().includes(search) ||
                    r.phone?.includes(search)
                  );
                })
                .map((rider) => (
                <div 
                  key={rider.id} 
                  className="p-4 bg-card rounded-xl border border-border flex flex-col md:flex-row md:items-center justify-between gap-4"
                  data-testid={`rider-request-${rider.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <UserPlus className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{rider.name}</h4>
                      <p className="text-sm text-muted-foreground">{rider.phone}</p>
                      {rider.vehicle_type && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Vehicle: {rider.vehicle_type} {rider.vehicle_number && `• ${rider.vehicle_number}`}
                        </p>
                      )}
                      {rider.registration_location && (
                        <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {rider.registration_location.address || `${rider.registration_location.lat?.toFixed(4)}, ${rider.registration_location.lng?.toFixed(4)}`}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Signed up: {rider.created_at ? new Date(rider.created_at).toLocaleString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={() => openClaimDialog(rider)}
                      data-testid={`claim-rider-${rider.id}`}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add to My Drivers
                    </Button>
                    <Button 
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => dismissRiderRequest(rider.id, rider.name)}
                      data-testid={`dismiss-rider-${rider.id}`}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={fetchRiderRequests}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Driver Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Driver</DialogTitle>
            <p className="text-sm text-muted-foreground">Driver can login with Phone OTP</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} placeholder="Driver Name" />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} placeholder="Enter phone number" />
              <p className="text-xs text-muted-foreground mt-1">Driver will use this number to login with OTP</p>
            </div>
            <div>
              <Label>Vehicle Type</Label>
              <select
                value={newDriver.vehicle_type || ''}
                onChange={(e) => setNewDriver({ ...newDriver, vehicle_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-md bg-background"
              >
                <option value="">Select vehicle type</option>
                <option value="bike">Bike</option>
                <option value="auto">Auto</option>
                <option value="car">Car</option>
                <option value="goods_carrier">Goods Carrier</option>
              </select>
            </div>
            <div>
              <Label>Vehicle Number</Label>
              <Input value={newDriver.vehicle_number || ''} onChange={(e) => setNewDriver({ ...newDriver, vehicle_number: e.target.value.toUpperCase() })} placeholder="KA 01 AB 1234" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDriverByPhone}>Create Driver</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* City Assignment Dialog */}
      <Dialog open={showCityDialog} onOpenChange={setShowCityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign City to Driver</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {selectedDriver?.name} - Drivers will only see rides from their assigned city
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Select City</Label>
              <select
                value={selectedCityId}
                onChange={(e) => setSelectedCityId(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background"
                data-testid="city-select"
              >
                <option value="">No City (All Rides)</option>
                {sortedCities.map(city => (
                  <option key={city.id} value={city.id}>{city.name}, {city.state}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Drivers without a city assignment can see all available rides
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCityDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignCity} data-testid="save-city-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim Driver Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>Add Rider to Your Drivers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="font-medium">{selectedDriver?.name}</p>
              <p className="text-sm text-muted-foreground">{selectedDriver?.phone}</p>
              {selectedDriver?.vehicle_type && (
                <p className="text-sm text-muted-foreground mt-1">
                  Vehicle: {selectedDriver.vehicle_type} {selectedDriver.vehicle_number && `• ${selectedDriver.vehicle_number}`}
                </p>
              )}
            </div>
            <div>
              <Label>Assign to City (Optional)</Label>
              <p className="text-sm text-muted-foreground mb-2">
                You can assign this driver to one of your cities now or later
              </p>
              <Select value={claimCityId} onValueChange={setClaimCityId}>
                <SelectTrigger data-testid="claim-city-select">
                  <SelectValue placeholder="Select a city (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No City Assignment</SelectItem>
                  {sortedCities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}, {city.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleClaimDriver} 
              disabled={claimingDriver}
              data-testid="confirm-claim-btn"
            >
              {claimingDriver ? "Adding..." : "Add to My Drivers"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Management Dialog */}
      <Dialog open={showSubscriptionDialog} onOpenChange={setShowSubscriptionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription Management
            </DialogTitle>
          </DialogHeader>
          
          {subscriptionLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : subscriptionData ? (
            <div className="space-y-4">
              {/* Driver Info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{subscriptionData.driver_name}</p>
                <p className="text-sm text-muted-foreground">{subscriptionData.driver_phone}</p>
              </div>

              {subscriptionData.is_subscription ? (
                <>
                  {/* Current Plan */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Current Plan</Label>
                    <div className="p-3 border rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Plan Type:</span>
                        <span className="font-medium">{subscriptionData.subscription?.plan_name || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Rate:</span>
                        <span className="font-medium">₹{subscriptionData.subscription?.subscription_rate || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className={`font-medium ${subscriptionData.subscription?.is_active ? 'text-green-600' : 'text-red-600'}`}>
                          {subscriptionData.subscription?.is_active ? 'Active' : 'Expired/Inactive'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Start Date:</span>
                        <span className="font-medium">
                          {subscriptionData.subscription?.start_date 
                            ? new Date(subscriptionData.subscription.start_date).toLocaleDateString()
                            : 'Not set'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expiry Date:</span>
                        <span className={`font-medium ${subscriptionData.subscription?.days_until_expiry !== null && subscriptionData.subscription?.days_until_expiry <= 3 ? 'text-orange-600' : ''}`}>
                          {subscriptionData.subscription?.expiry_date 
                            ? new Date(subscriptionData.subscription.expiry_date).toLocaleDateString()
                            : 'Not set'}
                        </span>
                      </div>
                      {subscriptionData.subscription?.days_until_expiry !== null && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Days Left:</span>
                          <span className={`font-medium ${subscriptionData.subscription?.days_until_expiry <= 3 ? 'text-orange-600' : ''}`}>
                            {subscriptionData.subscription?.days_until_expiry < 0 
                              ? `Expired ${Math.abs(subscriptionData.subscription.days_until_expiry)} days ago`
                              : `${subscriptionData.subscription.days_until_expiry} days`
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Edit Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_date">Edit Start Date</Label>
                      <Input
                        id="start_date"
                        type="datetime-local"
                        defaultValue={subscriptionData.subscription?.start_date ? subscriptionData.subscription.start_date.slice(0, 16) : ''}
                        key={`start-${subscriptionData.subscription?.start_date}`}
                        onBlur={(e) => {
                          if (e.target.value) {
                            const date = new Date(e.target.value).toISOString();
                            updateSubscription({ start_date: date });
                          }
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expiry_date">Edit Expiry Date</Label>
                      <Input
                        id="expiry_date"
                        type="datetime-local"
                        defaultValue={subscriptionData.subscription?.expiry_date ? subscriptionData.subscription.expiry_date.slice(0, 16) : ''}
                        key={`expiry-${subscriptionData.subscription?.expiry_date}`}
                        onBlur={(e) => {
                          if (e.target.value) {
                            const date = new Date(e.target.value).toISOString();
                            updateSubscription({ expiry_date: date });
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Rate */}
                  <div className="space-y-2">
                    <Label htmlFor="rate">Subscription Rate (₹)</Label>
                    <Input
                      id="rate"
                      type="number"
                      defaultValue={subscriptionData.subscription?.subscription_rate || ''}
                      key={`rate-${subscriptionData.subscription?.subscription_rate}`}
                      onBlur={(e) => {
                        const rate = parseFloat(e.target.value);
                        if (rate > 0) {
                          updateSubscription({ subscription_rate: rate });
                        }
                      }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    {subscriptionData.subscription?.is_active ? (
                      <Button 
                        variant="destructive" 
                        className="flex-1"
                        onClick={deactivateSubscription}
                      >
                        <Pause className="w-4 h-4 mr-2" />
                        Deactivate
                      </Button>
                    ) : (
                      <Button 
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => activateSubscription()}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Activate
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        const days = prompt("Extend by how many days?", "7");
                        if (days && !isNaN(parseInt(days))) {
                          activateSubscription(parseInt(days));
                        }
                      }}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Extend
                    </Button>
                  </div>

                  {/* Carried Over Dues */}
                  {subscriptionData.subscription?.carried_over_dues > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Carried Over Dues:</strong> ₹{subscriptionData.subscription.carried_over_dues}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>This driver is not on a subscription plan.</p>
                  <p className="text-sm mt-2">They are using per-ride commission.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>Failed to load subscription details.</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg" data-testid="edit-driver-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Driver Details
            </DialogTitle>
          </DialogHeader>
          
          {editingDriver && (
            <div className="space-y-4 py-4">
              {/* Name */}
              <div>
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editingDriver.name || ""}
                  onChange={(e) => setEditingDriver({...editingDriver, name: e.target.value})}
                  placeholder="Driver name"
                  data-testid="edit-driver-name"
                />
              </div>
              
              {/* Phone */}
              <div>
                <Label htmlFor="edit-phone">Phone Number</Label>
                <Input
                  id="edit-phone"
                  value={editingDriver.phone || ""}
                  onChange={(e) => setEditingDriver({...editingDriver, phone: e.target.value})}
                  placeholder="Phone number"
                  data-testid="edit-driver-phone"
                />
              </div>
              
              {/* Vehicle Type */}
              <div>
                <Label htmlFor="edit-vehicle-type">Vehicle Type</Label>
                <Select 
                  value={editingDriver.vehicle_type || ""} 
                  onValueChange={(val) => setEditingDriver({...editingDriver, vehicle_type: val, vehicle_subtype: null})}
                >
                  <SelectTrigger data-testid="edit-vehicle-type">
                    <SelectValue placeholder="Select vehicle type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bike">Bike</SelectItem>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="goods_carrier">Goods Carrier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Vehicle Subtype */}
              {editingDriver.vehicle_type && vehicleSubtypes.find(v => v.vehicle_type === editingDriver.vehicle_type)?.subtypes?.length > 0 && (
                <div>
                  <Label htmlFor="edit-vehicle-subtype">Vehicle Subtype</Label>
                  <Select 
                    value={editingDriver.vehicle_subtype || "__none__"} 
                    onValueChange={(val) => setEditingDriver({...editingDriver, vehicle_subtype: val === "__none__" ? null : val})}
                  >
                    <SelectTrigger data-testid="edit-vehicle-subtype">
                      <SelectValue placeholder="Select subtype (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {vehicleSubtypes
                        .find(v => v.vehicle_type === editingDriver.vehicle_type)
                        ?.subtypes?.filter(s => s.is_active)
                        .map((subtype) => (
                          <SelectItem key={subtype.id} value={subtype.id}>
                            {subtype.display_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Vehicle Number */}
              <div>
                <Label htmlFor="edit-vehicle-number">Vehicle Number</Label>
                <Input
                  id="edit-vehicle-number"
                  value={editingDriver.vehicle_number || ""}
                  onChange={(e) => setEditingDriver({...editingDriver, vehicle_number: e.target.value.toUpperCase()})}
                  placeholder="e.g., AP 09 AB 1234"
                  data-testid="edit-vehicle-number"
                />
              </div>
              
              {/* City Assignment */}
              <div>
                <Label htmlFor="edit-city">City Assignment</Label>
                <Select 
                  value={editingDriver.city_id || "__none__"} 
                  onValueChange={(val) => setEditingDriver({...editingDriver, city_id: val === "__none__" ? null : val})}
                >
                  <SelectTrigger data-testid="edit-city-select">
                    <SelectValue placeholder="Select city (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No City</SelectItem>
                    {sortedCities.map((city) => (
                      <SelectItem key={city.id} value={city.id}>
                        {city.name}, {city.state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Commission Plan */}
              <div>
                <Label htmlFor="edit-commission-plan">Commission Plan</Label>
                <Select 
                  value={editingDriver.commission_plan_id || "__none__"} 
                  onValueChange={(val) => setEditingDriver({...editingDriver, commission_plan_id: val === "__none__" ? null : val})}
                >
                  <SelectTrigger data-testid="edit-commission-plan">
                    <SelectValue placeholder="Select commission plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Default (Per Ride)</SelectItem>
                    {commissionPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - {plan.type === 'fixed_per_ride' ? `₹${plan.fixed_amount}/ride` : 
                          plan.type === 'percentage' ? `${plan.percentage}%` : 
                          plan.type === 'monthly' ? `₹${plan.monthly_amount}/month` : plan.type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bank Details - Read Only Display */}
              {editingDriver.bank_details?.account_number && (
                <div className="border-t pt-4 mt-4">
                  <Label className="flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4" />
                    Bank Details (for payments)
                  </Label>
                  <div className="bg-green-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Account Holder:</span>
                      <span className="font-medium">{editingDriver.bank_details.account_holder_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Account Number:</span>
                      <span className="font-medium font-mono">****{editingDriver.bank_details.account_number.slice(-4)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bank:</span>
                      <span className="font-medium">{editingDriver.bank_details.bank_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">IFSC:</span>
                      <span className="font-medium font-mono">{editingDriver.bank_details.ifsc_code || '-'}</span>
                    </div>
                    {editingDriver.bank_details.mobile_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mobile:</span>
                        <span className="font-medium">{editingDriver.bank_details.mobile_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!editingDriver.bank_details?.account_number && (
                <div className="border-t pt-4 mt-4">
                  <div className="bg-yellow-50 rounded-lg p-4 flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">No Bank Details</p>
                      <p className="text-xs text-yellow-600">Rider has not added bank details for payments yet</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateDriver} data-testid="save-driver-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversManagement;
